import {MAX_FILE_SIZE as DIRECT_MAX_FILE_SIZE} from '../home_media_ui/direct';
import {cancelledUpload} from '../utils/completionState';
import { getUploadRecovery, cancelInterruptedUpload, uploadResumePath } from '../utils/uploadRecovery';
import SurveyorUploadProgress from '../components/SurveyorUploadProgress';
import SurveyorAdditionalActions from '../components/Quality/SurveyorAdditionalActions';
import { hasAdditional } from '../components/Quality/additionalActionsUi';
import { arrivalReady } from '../dispatchUi';
import { Box, Stack, Typography, Button, Sheet, CircularProgress, IconButton, Textarea, Select, Option, Alert } from "@mui/joy";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { LayoutContext } from "../context";
import { APIGetEventById } from "../api";

import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import CloseRoundedIcon         from '@mui/icons-material/CloseRounded';
import ImageRoundedIcon         from '@mui/icons-material/ImageRounded';
import VideoFileRoundedIcon     from '@mui/icons-material/VideoFileRounded';
import CloudUploadRoundedIcon   from '@mui/icons-material/CloudUploadRounded';
import TaskAltRoundedIcon       from '@mui/icons-material/TaskAltRounded';
import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import WifiRoundedIcon          from '@mui/icons-material/WifiRounded';

// ─── Утилиты ─────────────────────────────────────────────────────────────────

function formatBytes(b) {
    if (!b) return '0 Б';
    if (b < 1024) return `${b} Б`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} КБ`;
    return `${(b / (1024 * 1024)).toFixed(1)} МБ`;
}
function formatSpeed(mbps) {
    if (!mbps || mbps <= 0) return '—';
    if (mbps < 1) return `${(mbps * 1000).toFixed(0)} Кбит/с`;
    return `${mbps.toFixed(1)} Мбит/с`;
}
async function createThumbnail(file, maxSize = 120) {
    if (!file.type?.startsWith('image/')) return null;

    // Задаём только ширину: браузер сохраняет пропорции, canvas ограничивает обе стороны.
    // Превью создаются последовательно, bitmap освобождаем после отрисовки.
    if (typeof createImageBitmap === 'function') {
        try {
            const bitmap = await createImageBitmap(file, { resizeWidth: maxSize, resizeQuality: 'low' });
            const ratio = Math.min(maxSize / bitmap.width, maxSize / bitmap.height, 1);
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(bitmap.width * ratio));
            canvas.height = Math.max(1, Math.round(bitmap.height * ratio));
            const ctx = canvas.getContext('2d');
            ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
            bitmap.close();

            const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.5));
            canvas.width = 0;
            canvas.height = 0;

            return blob ? URL.createObjectURL(blob) : null;
        } catch {
            return null;
        }
    }

    // Fallback для старых браузеров без createImageBitmap
    return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            URL.revokeObjectURL(url);
            canvas.toBlob((blob) => {
                canvas.width = 0;
                canvas.height = 0;
                resolve(blob ? URL.createObjectURL(blob) : null);
            }, 'image/jpeg', 0.5);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        img.src = url;
    });
}


// ─── Зона загрузки файлов (оптимизированная) ────────────────────────────────

const VISIBLE_FILES_LIMIT = 20;

function FileUploadZone({ files, setFiles, disabled, additional = false }) {
    const inputRef = useRef(null);
    const [showAll, setShowAll] = useState(false);

    const MAX_FILE_SIZE = DIRECT_MAX_FILE_SIZE;

    const handleSelect = async (e) => {
        const rawFiles = Array.from(e.target.files || []);
        e.target.value = '';

        // Отсеиваем слишком большие файлы
        const accepted = [];
        const rejected = [];
        for (const f of rawFiles) {
            if (f.size > MAX_FILE_SIZE) {
                rejected.push(f);
            } else {
                accepted.push(f);
            }
        }
        if (rejected.length > 0) {
            const names = rejected.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(0)} МБ)`).join('\n');
            alert(
                `Эти файлы слишком большие и не могут быть загружены:\n\n${names}\n\n` +
                `Максимальный размер одного файла — 2 ГиБ.\n` +
                `Запишите видео покороче или сожмите его перед загрузкой.`
            );
        }
        if (accepted.length === 0) return;

        // Сначала добавляем файлы БЕЗ превью — мгновенно
        const placeholders = accepted.map(file => ({ file, thumbUrl: null, fileType: file.type === 'application/pdf' ? 'survey_sheet' : 'room_photo' }));
        setFiles(prev => [...prev, ...placeholders]);

        // Затем последовательно генерируем превью только для картинок
        for (let i = 0; i < accepted.length; i++) {
            const file = accepted[i];
            if (!file.type?.startsWith('image/')) continue;

            await new Promise(r => setTimeout(r, 0));

            const thumb = await createThumbnail(file);
            if (!thumb) continue;

            setFiles(prev => {
                const idx = prev.findIndex(it => it.file === file);
                if (idx === -1) {
                    if (thumb.startsWith?.('blob:')) URL.revokeObjectURL(thumb);
                    return prev;
                }
                const next = prev.slice();
                next[idx] = { ...next[idx], thumbUrl: thumb };
                return next;
            });
        }
    };

    const remove = (idx) => {
        setFiles(prev => {
            const removed = prev[idx];
            if (removed?.thumbUrl?.startsWith?.('blob:')) {
                URL.revokeObjectURL(removed.thumbUrl);
            }
            return prev.filter((_, i) => i !== idx);
        });
    };

    const visibleFiles = showAll ? files : files.slice(0, VISIBLE_FILES_LIMIT);
    const hiddenCount = files.length - visibleFiles.length;

    return (
        <Box sx={{ opacity: disabled ? 0.55 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
            <Box onClick={() => inputRef.current?.click()} sx={{
                border: '2px dashed rgba(15,123,171,0.3)', borderRadius: '16px', p: 3,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                cursor: 'pointer', background: 'rgba(15,123,171,0.03)', transition: 'all 0.2s',
                '&:hover': { borderColor: '#0f7bab', background: 'rgba(15,123,171,0.07)' },
            }}>
                <Box sx={{ width: 48, height: 48, borderRadius: '14px', background: 'rgba(15,123,171,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f7bab' }}>
                    <AddRoundedIcon sx={{ fontSize: 28 }} />
                </Box>
                <Typography level="body-sm" sx={{ fontWeight: 700, color: '#0f7bab' }}>Добавить фото / видео</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center' }}>Фотографии и видеозаписи помещений</Typography>
            </Box>
            <input ref={inputRef} type="file" accept={additional ? "image/*,video/*,application/pdf" : "image/*,video/*"} multiple style={{ display: 'none' }} onChange={handleSelect} />

            {files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography level="body-xs" sx={{ mb: 1, color: 'text.tertiary', fontWeight: 600 }}>
                        Выбрано файлов: {files.length}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                        {visibleFiles.map((item, idx) => {
                            const { file, thumbUrl } = item;
                            const isImg = file.type?.startsWith('image/');
                            const color = isImg ? '#0f7bab' : '#b07d00';
                            return (
                                <Box key={`${file.name}-${idx}`} sx={{ position: 'relative' }}>
                                    <IconButton size="sm" onClick={() => remove(idx)} sx={{ position: 'absolute', top: -8, right: -8, zIndex: 2, background: '#ff4d4f', color: 'white', width: 22, height: 22, minWidth: 22, minHeight: 22, borderRadius: '50%', '&:hover': { background: '#d9363e' } }}>
                                        <CloseRoundedIcon sx={{ fontSize: 13 }} />
                                    </IconButton>

                                    {additional && <Select size="sm" value={item.fileType || 'room_photo'} sx={{ width: 150, mb: 1 }}
                                      onChange={(_, value) => setFiles(prev => prev.map((entry, i) => i === idx ? { ...entry, fileType: value } : entry))}>
                                      <Option value="room_photo">Фото / видео</Option><Option value="survey_sheet">Обмерный лист</Option>
                                    </Select>}
                                    {thumbUrl ? (
                                        <Box sx={{ width: 90, height: 90, borderRadius: '14px', overflow: 'hidden', border: `2px solid ${color}30` }}>
                                            <img src={thumbUrl} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        </Box>
                                    ) : (
                                        <Box sx={{ width: 90, height: 90, borderRadius: '14px', background: 'rgba(176,125,0,0.1)', border: `2px solid ${color}30`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, p: 1 }}>
                                            <VideoFileRoundedIcon sx={{ color, fontSize: 22 }} />
                                            <Typography level="body-xs" sx={{ fontWeight: 600, color, fontSize: '0.6rem', textAlign: 'center', wordBreak: 'break-all', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{file.name}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                    {hiddenCount > 0 && (
                        <Button variant="plain" size="sm" onClick={() => setShowAll(true)} sx={{ mt: 1, fontWeight: 600 }}>
                            Показать ещё {hiddenCount} файлов
                        </Button>
                    )}
                    {showAll && files.length > VISIBLE_FILES_LIMIT && (
                        <Button variant="plain" size="sm" onClick={() => setShowAll(false)} sx={{ mt: 1, fontWeight: 600 }}>
                            Свернуть
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}

// ─── Блок загрузки ────────────────────────────────────────────────────────────

function UploadBlock(props) { return <SurveyorUploadProgress {...props} supplement waitForProcessing />; }

export default function SurveyorSupplementPage() {
    const { bitrixId } = useParams(); 
    const [searchParams] = useSearchParams();
    const explicitEventId = searchParams.get('event');
    const expectedCycleId = searchParams.get('additional');
    const requestedProcessId = searchParams.get('process');
    const [loadedEvent, setLoadedEvent] = useState(null);
    const [eventError, setEventError] = useState('');
    const [processChecking, setProcessChecking] = useState(true);
    const [processCheckError, setProcessCheckError] = useState('');
    const [processCheckKey, setProcessCheckKey] = useState(0);
    const [existingProcess, setExistingProcess] = useState(null);
    const [resumeProcessId, setResumeProcessId] = useState(null);
    const [retryProcessing, setRetryProcessing] = useState(false);
    const [recoveryBusy, setRecoveryBusy] = useState(false);
    const [recoveryError, setRecoveryError] = useState('');
    const [comment, setComment] = useState('Результат дополнительного обмера');
    const navigate = useNavigate();
    const { tokenEmployee, listEvents, account, updateListEvents } = useContext(LayoutContext);

    const [files, setFiles]         = useState([]);
    const [uploadInFlight, setUploadInFlight] = useState(false);
    const [pendingTransfer, setPendingTransfer] = useState(false);
    const [phase, setPhase]         = useState('idle'); // idle | checking | ready_to_upload | uploading | done
    const [speedInfo, setSpeedInfo] = useState(null);
    const [vpnDetected, setVpnDetected] = useState(false);

    const cachedEvent = useMemo(() => {
        const matches = (listEvents || []).filter(e => String(e.bitrix_deal?.id) === String(bitrixId));
        return explicitEventId ? matches.find(e => String(e.id) === explicitEventId) || null : matches.length === 1 ? matches[0] : null;
    }, [bitrixId, explicitEventId, listEvents]);
    const candidateId = explicitEventId || cachedEvent?.id;
    const loadedScope = useRef('');
    const recoveryScope = useRef('');
    const identity = `${candidateId || ''}:${tokenEmployee || ''}:${account?.id || ''}:${expectedCycleId || ''}:${requestedProcessId || ''}`;
    const previousIdentity = useRef(identity), identityEpoch = useRef(0);
    if (previousIdentity.current !== identity) { previousIdentity.current = identity; identityEpoch.current++; }
    recoveryScope.current = `${identity}:${identityEpoch.current}`;
    const selectedEvent = loadedScope.current === recoveryScope.current ? loadedEvent : null;
    useEffect(() => {
        let disposed = false;
        const scope = recoveryScope.current;
        setLoadedEvent(null); setEventError('');
        setProcessChecking(true); setProcessCheckError(''); setExistingProcess(null);
        setResumeProcessId(null); setRetryProcessing(false); setPhase('idle'); setRecoveryBusy(false); setRecoveryError('');
        if (!candidateId || !tokenEmployee) {
            setProcessChecking(false);
            return () => { disposed = true; };
        }
        (async () => {
            let event;
            try {
                event = await APIGetEventById(candidateId, tokenEmployee);
                if (disposed) return;
                const ownerId = typeof event?.employee === 'object' ? event.employee?.id : event?.employee;
                if (!event?.id || String(event.bitrix_deal?.id) !== String(bitrixId) || String(ownerId) !== String(account?.id)) {
                    setEventError('Не удалось загрузить назначенное вам событие'); return;
                }
                loadedScope.current = scope;
                setLoadedEvent(event);
            } catch {
                if (!disposed) setEventError('Не удалось загрузить событие');
                return;
            }
            try {
                const additional = hasAdditional(event) || expectedCycleId !== null;
                const currentCycleId = additional ? event.additional_measurement?.id : null;
                const expectedType = additional ? 'completion' : 'supplement';
                const process = await getUploadRecovery(event.id, tokenEmployee, {
                    processId: requestedProcessId, intent: expectedType, additionalId: currentCycleId,
                });
                if (disposed) return;
                if (typeof process?.has_process !== 'boolean') throw new Error('Не удалось проверить предыдущую загрузку. Повторите проверку; новую загрузку пока не начинаем.');
                if (!process.has_process) return;
                if (cancelledUpload(process)) {
                    if (requestedProcessId) {
                        const query = new URLSearchParams(searchParams); query.delete('process');
                        navigate(`/surveyor/supplement/${bitrixId}?${query}`, { replace: true });
                    }
                    return;
                }
                if (expectedCycleId && String(currentCycleId) !== expectedCycleId) return;
                // Старый неудачный дозамер не относится к новому назначению.
                // Новое поле приходит вместе с серверным обновлением; без него
                // не угадываем принадлежность процесса текущему циклу.
                if (!Object.prototype.hasOwnProperty.call(process, 'additional_measurement_id')) {
                    throw new Error('Обновите сервер и повторите проверку предыдущей загрузки.');
                }
                const sameCycle = additional
                    ? currentCycleId != null && String(process.additional_measurement_id) === String(currentCycleId)
                    : process.additional_measurement_id == null;
                if (process.process_type !== expectedType || !sameCycle) {
                    if (['uploading', 'pending', 'processing'].includes(process.status)) {
                        setExistingProcess({ ...process, intent_matches: false });
                    }
                    return;
                }
                if (process.status === 'done') {
                    // Процесс мог завершиться между чтением события и статуса.
                    // Текущий дозамер уже закрыт; не предлагаем завершить его снова.
                    if (additional || requestedProcessId) {
                        setPendingTransfer(Boolean(process.transfer_pending)); setPhase('done');
                        Promise.resolve(updateListEvents(null, account.id)).catch(() => {});
                    }
                    return;
                }
                if (!process.process_id || !['uploading', 'pending', 'processing', 'failed'].includes(process.status)) {
                    throw new Error('Сервер не подтвердил статус предыдущей загрузки. Повторите проверку.');
                }
                setResumeProcessId(process.process_id);
                if (process.comment) setComment(process.comment);
                if (['pending', 'processing'].includes(process.status)) {
                    setFiles([]); setPhase('uploading');
                } else setExistingProcess(process);
            } catch (error) {
                if (!disposed) setProcessCheckError(error.message || 'Не удалось проверить предыдущую загрузку.');
            }
        })().finally(() => { if (!disposed) setProcessChecking(false); });
        return () => { disposed = true; };
    }, [candidateId, bitrixId, tokenEmployee, account?.id, expectedCycleId, requestedProcessId, processCheckKey]);
    const isAdditional = hasAdditional(selectedEvent) || expectedCycleId !== null;
    const cycle = selectedEvent?.additional_measurement;
    const cycleMatches = !expectedCycleId || (cycle?.status === 'active' && String(cycle.id) === expectedCycleId);
    const canUpload = Boolean(selectedEvent && !eventError && !processChecking && !processCheckError && !existingProcess && (isAdditional
        ? cycle?.status === 'active' && cycle?.accepted_at && arrivalReady(selectedEvent) && cycleMatches && comment.trim().length >= 5 && comment.length <= 4000
        : selectedEvent.is_completed && ['drawing', 'completed'].includes(selectedEvent.state)));

    useEffect(() => {
        if (account?.id && account?.position?.includes('surveyor')) {
            updateListEvents(null, account.id);
        }
    }, [account?.id]);

    useEffect(() => {
        setFiles([]); setPhase('idle'); setSpeedInfo(null); setVpnDetected(false);
        setUploadInFlight(false); setPendingTransfer(false);
        setComment('Результат дополнительного обмера');
    }, [bitrixId, explicitEventId, expectedCycleId, requestedProcessId, tokenEmployee, account?.id]);

    const continueExisting = () => {
        const process = existingProcess;
        if (!process) return;
        if (process.intent_matches === false) {
            const path = uploadResumePath(process.resume_url, selectedEvent.id);
            if (path) navigate(path); else setRecoveryError('Не удалось определить страницу передачи. Обновите статус.');
            return;
        }
        setResumeProcessId(process.process_id); setRetryProcessing(false); setExistingProcess(null);
    };
    const stopInterrupted = async () => {
        if (recoveryBusy || !existingProcess?.can_cancel_upload) return;
        if (!window.confirm(`Отменить незавершённую передачу №${existingProcess.process_id}? Приостановите её во всех вкладках. Уже принятые файлы сохранятся; непереданные файлы можно будет выбрать заново.`)) return;
        const scope = recoveryScope.current;
        setRecoveryBusy(true); setRecoveryError('');
        try {
            await cancelInterruptedUpload(selectedEvent.id, existingProcess, tokenEmployee);
            if (recoveryScope.current !== scope) return;
            setResumeProcessId(null); setRetryProcessing(false); setExistingProcess(null);
            if (requestedProcessId) {
                const query = new URLSearchParams(searchParams); query.delete('process');
                navigate(`/surveyor/supplement/${bitrixId}?${query}`, { replace: true });
            } else setProcessCheckKey(value => value + 1);
        } catch (error) { if (recoveryScope.current === scope) setRecoveryError(error.message); }
        finally { if (recoveryScope.current === scope) setRecoveryBusy(false); }
    };

    const handleFinishClick = () => {
        if (!files.length || !canUpload) return;
        setSpeedInfo({ label: 'Загрузка с продолжением', color: '#572AC3', verdict: 'При обрыве повторим неполученную часть. Слабая связь увеличит время загрузки.' });
        setPhase('ready_to_upload');
    };
    const handleVpnConfirm = handleFinishClick;
    const handleVpnSkip = handleFinishClick;

    const handleStartUpload = () => { if (canUpload) setPhase('uploading'); };

    const handleDone = result => {
        if (!result?.success) return;
        setPendingTransfer(Boolean(result.transfer_pending)); setPhase('done');
        Promise.resolve(updateListEvents(null, account.id)).catch(() => {});
    };

    return (
        <Box sx={{ maxWidth: 560, mx: 'auto', px: 2, pt: 3, pb: 6 }}>
            {/* Шапка */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <IconButton variant="soft" color="neutral" disabled={uploadInFlight} onClick={() => navigate(-1)} sx={{ borderRadius: '12px' }}>
                    <ArrowBackRoundedIcon />
                </IconButton>
                <Box>
                    <Typography level="title-lg" sx={{ fontWeight: 800, color: '#1a1a2e' }}>{isAdditional ? 'Завершение дозамера' : 'Дополнение обмера'}</Typography>
                    {selectedEvent?.bitrix_deal?.id && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Сделка №{selectedEvent.bitrix_deal.id}</Typography>
                    )}
                </Box>
            </Stack>

            {phase !== 'uploading' && phase !== 'done' && <>
                {eventError && <Alert color="danger" sx={{ mb: 2 }}>{eventError}</Alert>}
                {!selectedEvent && !eventError && <Alert sx={{ mb: 2 }}>Загружаем событие. Если ссылка не определяет событие, откройте дополнение из его карточки.</Alert>}
                {isAdditional && selectedEvent && <SurveyorAdditionalActions event={selectedEvent} onChanged={setLoadedEvent} showFinish={false} />}
                {isAdditional && cycle?.accepted_at && !arrivalReady(selectedEvent) && <Alert color="warning" sx={{ mb: 2 }} endDecorator={<Button size="sm" onClick={() => navigate(`/surveyor/arrive/${selectedEvent.id}`)}>Я на месте</Button>}>Сначала отметьте прибытие на текущий допобмер.</Alert>}
                {isAdditional && !cycleMatches && <Alert color="danger" sx={{ mb: 2 }}>Назначение дозамера изменилось. Откройте страницу из актуальной карточки.</Alert>}
                {isAdditional && <Textarea minRows={2} sx={{ mb: 2 }} value={comment} onChange={e => setComment(e.target.value)} placeholder="Что уточнено и исправлено" />}
            </>}
            {phase === 'done' ? (
                <Sheet variant="soft" color="success" sx={{ p: 3, borderRadius: 16, textAlign: 'center' }}>
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 18 }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2, background: 'rgba(31,122,31,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TaskAltRoundedIcon sx={{ fontSize: 36, color: '#1f7a1f' }} />
                        </Box>
                    </motion.div>
                    <Typography level="title-lg" sx={{ fontWeight: 800, color: '#1f7a1f', mb: 0.5 }}>Файлы успешно загружены!</Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 2 }}>{pendingTransfer ? 'Файлы сохранены на основном сервере. Они автоматически перенесутся в домашнее хранилище.' : 'Материалы сохранены и переданы на обработку.'}</Typography>
                    <Button variant="solid" color="success" onClick={() => navigate(-1)} sx={{ borderRadius: '12px', fontWeight: 700 }}>
                        Вернуться назад
                    </Button>
                </Sheet>
            ) : (
                <Sheet sx={{ borderRadius: '18px', p: 2.5, boxShadow: '0 2px 12px rgba(15,123,171,0.08)' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <ImageRoundedIcon sx={{ color: '#0f7bab', fontSize: 20 }} />
                        <Typography level="title-sm" sx={{ fontWeight: 700, color: '#1a1a2e' }}>{isAdditional ? 'Материалы дозамера' : 'Фото и видео дополнения'}</Typography>
                    </Stack>
                    <Typography level="body-xs" sx={{ mb: 2, color: 'text.tertiary', lineHeight: 1.6 }}>
                        {isAdditional ? 'Материалы сохраняются в папке текущего дозамера' : 'Файлы будут загружены в папку «Дополнение обмера»'}
                    </Typography>

                    {phase === 'idle' && (processChecking || processCheckError) && (
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                            {processChecking ? <><CircularProgress /><Typography sx={{ mt: 1.5 }}>Проверяем предыдущую загрузку…</Typography></> : <>
                                <Alert color="warning" sx={{ mb: 2 }}>{processCheckError}</Alert>
                                <Button onClick={() => setProcessCheckKey(value => value + 1)}>Повторить проверку</Button>
                                {requestedProcessId && <Button variant="plain" onClick={() => {
                                    const query = new URLSearchParams(searchParams); query.delete('process');
                                    navigate(`/surveyor/supplement/${bitrixId}?${query}`, { replace: true });
                                }}>Показать актуальную передачу</Button>}
                            </>}
                        </Box>
                    )}
                    {phase === 'idle' && !processChecking && !processCheckError && existingProcess && (
                        <Stack spacing={1.5} sx={{ py: 2 }}>
                            <Typography level="title-md">{existingProcess.status === 'failed' ? 'Завершение не подтверждено'
                                : existingProcess.intent_matches === false ? 'Есть незавершённая передача файлов'
                                : existingProcess.status === 'uploading' ? 'Можно продолжить загрузку' : 'Файлы обрабатываются'}</Typography>
                            <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
                                {existingProcess.status === 'failed'
                                    ? existingProcess.retry_reason || existingProcess.error_message || 'Файлы сохранены, но завершение не подтверждено. Сообщите менеджеру номер загрузки.'
                                    : existingProcess.resume_problem || (existingProcess.intent_matches === false
                                        ? 'Ранее начата передача другого вида. Откройте её и продолжите с сохранённого места. Параллельно новую передачу не запускаем.'
                                        : 'Выберите оставшиеся исходные файлы. Уже полученные части сохранятся; принятые файлы повторно отправлять не нужно. Не загружайте одновременно в двух вкладках.')}
                            </Typography>
                            <Typography level="body-xs">Загрузка №{existingProcess.process_id}</Typography>
                            {Array.isArray(existingProcess.unfinished_files) && existingProcess.unfinished_files.length > 0 && <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'sm' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Незавершённые файлы (размер исходника):</Typography>
                                {existingProcess.unfinished_files.slice(0, 8).map(file => <Typography key={file.upload_id} level="body-sm" sx={{ overflowWrap: 'anywhere' }}>
                                    {file.file_name} · {formatBytes(file.total_size)}
                                </Typography>)}
                                {existingProcess.unfinished_files.length > 8 && <Typography level="body-xs">Ещё {existingProcess.unfinished_files.length - 8} файлов</Typography>}
                            </Sheet>}
                            {existingProcess.ready_files > 0 && <Typography level="body-xs">Уже принято файлов: {existingProcess.ready_files}. Они сохранятся при продолжении или явной отмене.</Typography>}
                            {recoveryError && <Alert color="warning" sx={{ overflowWrap: 'anywhere' }}>{recoveryError}</Alert>}
                            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                                {existingProcess.status === 'uploading' && existingProcess.can_resume_upload !== false && <Button disabled={recoveryBusy} onClick={continueExisting}>
                                    {existingProcess.intent_matches === false ? 'Открыть начатую загрузку' : 'Продолжить загрузку'}
                                </Button>}
                                {existingProcess.status === 'uploading' && existingProcess.can_resume_upload !== false && existingProcess.intent_matches !== false && existingProcess.all_files_received && <Button disabled={recoveryBusy} onClick={() => {
                                    setResumeProcessId(existingProcess.process_id); setRetryProcessing(false); setFiles([]); setExistingProcess(null); setPhase('uploading');
                                }}>Завершить передачу принятых файлов</Button>}
                                {['pending', 'processing'].includes(existingProcess.status) && existingProcess.intent_matches === false && <Button disabled={recoveryBusy} onClick={continueExisting}>Открыть обработку файлов</Button>}
                                {existingProcess.status === 'failed' && existingProcess.retryable && <Button onClick={() => {
                                    setResumeProcessId(existingProcess.process_id); setRetryProcessing(true);
                                    setFiles([]); setExistingProcess(null); setPhase('uploading');
                                }}>Повторить завершение</Button>}
                                {existingProcess.can_cancel_upload && <Button variant="outlined" color="danger" loading={recoveryBusy} onClick={stopInterrupted}>Отменить незавершённую передачу</Button>}
                                {requestedProcessId && <Button variant="plain" disabled={recoveryBusy} onClick={() => {
                                    const query = new URLSearchParams(searchParams); query.delete('process');
                                    navigate(`/surveyor/supplement/${bitrixId}?${query}`, { replace: true });
                                }}>Показать актуальную передачу</Button>}
                                <Button variant="outlined" disabled={recoveryBusy} onClick={() => setProcessCheckKey(value => value + 1)}>Обновить статус</Button>
                                <Button variant="plain" onClick={() => navigate(-1)}>Вернуться назад</Button>
                            </Box>
                        </Stack>
                    )}
                    {phase === 'idle' && !processChecking && !processCheckError && !existingProcess && (
                        <>
                            <FileUploadZone files={files} setFiles={setFiles} disabled={!selectedEvent} additional={isAdditional} />
                            <Button fullWidth size="lg" variant="solid" onClick={handleFinishClick} disabled={!files.length || !canUpload}
                                endDecorator={<CloudUploadRoundedIcon />}
                                sx={{ mt: 2, borderRadius: '14px', fontWeight: 800, py: 1.5, background: files.length ? 'linear-gradient(135deg, #0f7bab 0%, #3aadd4 100%)' : undefined }}>
                                {isAdditional ? "Завершить дозамер" : "Загрузить"} ({files.length})
                            </Button>
                        </>
                    )}

                    {phase === 'vpn_check' && (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            <CircularProgress size="md" />
                            <Typography level="body-sm" sx={{ mt: 1.5 }}>Проверяем сетевое подключение...</Typography>
                        </Box>
                    )}

                    {phase === 'vpn_recheck' && (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            <CircularProgress size="md" />
                            <Typography level="body-sm" sx={{ mt: 1.5 }}>Перепроверяем подключение...</Typography>
                        </Box>
                    )}

                    {phase === 'vpn_warning' && (
                        <Box sx={{ mt: 1 }}>
                            <Sheet
                                variant="soft"
                                color="warning"
                                sx={{
                                    p: 2, borderRadius: 12, mb: 2,
                                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                }}
                            >
                                <Box sx={{
                                    p: 0.75, borderRadius: '50%',
                                    bgcolor: 'rgba(255,180,60,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0, mt: 0.25,
                                }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#b07d00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                        <line x1="12" y1="9" x2="12" y2="13" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                </Box>
                                <Box>
                                    <Typography level="title-sm" sx={{ fontWeight: 700, color: '#7a5a00', mb: 0.5 }}>
                                        Обнаружен VPN
                                    </Typography>
                                    <Typography level="body-sm" sx={{ color: '#7a5a00' }}>
                                        Загрузка файлов через VPN может работать очень медленно
                                        или прерываться. Пожалуйста, выключите VPN и нажмите
                                        «Я выключил VPN».
                                    </Typography>
                                </Box>
                            </Sheet>
                            <Stack spacing={1.5}>
                                <Button
                                    fullWidth size="lg" variant="solid"
                                    onClick={handleVpnConfirm}
                                    sx={{
                                        borderRadius: '14px', fontWeight: 800, py: 1.5,
                                        background: 'linear-gradient(135deg, #0f7bab 0%, #3aadd4 100%)',
                                    }}
                                >
                                    Я выключил VPN
                                </Button>
                                <Button
                                    fullWidth size="sm" variant="plain" color="neutral"
                                    onClick={handleVpnSkip}
                                    sx={{ borderRadius: '14px', fontWeight: 600 }}
                                >
                                    Продолжить с VPN
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {phase === 'vpn_warning_final' && (
                        <Box sx={{ mt: 1 }}>
                            <Sheet
                                variant="soft"
                                color="danger"
                                sx={{
                                    p: 2, borderRadius: 12, mb: 2,
                                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                }}
                            >
                                <Box sx={{
                                    p: 0.75, borderRadius: '50%',
                                    bgcolor: 'rgba(195,0,19,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0, mt: 0.25,
                                }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c30013" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                        <line x1="12" y1="9" x2="12" y2="13" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                </Box>
                                <Box>
                                    <Typography level="title-sm" sx={{ fontWeight: 700, color: '#9e000e', mb: 0.5 }}>
                                        VPN всё ещё включён
                                    </Typography>
                                    <Typography level="body-sm" sx={{ color: '#9e000e' }}>
                                        Мы по-прежнему видим VPN-подключение.
                                        Вы можете продолжить, но загрузка может быть нестабильной.
                                    </Typography>
                                </Box>
                            </Sheet>
                            <Stack spacing={1.5}>
                                <Button
                                    fullWidth size="lg" variant="solid"
                                    onClick={handleVpnConfirm}
                                    sx={{
                                        borderRadius: '14px', fontWeight: 800, py: 1.5,
                                        background: 'linear-gradient(135deg, #0f7bab 0%, #3aadd4 100%)',
                                    }}
                                >
                                    Попробовать ещё раз
                                </Button>
                                <Button
                                    fullWidth size="sm" variant="soft" color="danger"
                                    onClick={handleVpnSkip}
                                    sx={{ borderRadius: '14px', fontWeight: 600 }}
                                >
                                    Продолжить с VPN
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {phase === 'checking' && (
                        <Box sx={{ py: 3, textAlign: 'center' }}>
                            <CircularProgress size="md" />
                            <Typography level="body-sm" sx={{ mt: 1.5 }}>Проверяем связь...</Typography>
                        </Box>
                    )}

                    {phase === 'ready_to_upload' && speedInfo && (
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, p: 1.5, borderRadius: 12, background: 'rgba(15,123,171,0.04)' }}>
                                <WifiRoundedIcon sx={{ color: speedInfo.color, fontSize: 26 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography level="body-sm" sx={{ fontWeight: 700, color: speedInfo.color }}>{speedInfo.label}</Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{speedInfo.verdict}</Typography>
                                </Box>
                            </Stack>
                            <Button fullWidth size="lg" variant="solid" onClick={handleStartUpload}
                                endDecorator={<CloudUploadRoundedIcon />}
                                sx={{ borderRadius: '14px', fontWeight: 800, py: 1.5, background: 'linear-gradient(135deg, #0f7bab 0%, #3aadd4 100%)' }}>
                                Начать выгрузку
                            </Button>
                        </Box>
                    )}

                    {phase === 'uploading' && (
                        selectedEvent?.id ? (
                            <UploadBlock
                                key={`${selectedEvent.id}:${resumeProcessId || 'new'}:${isAdditional ? cycle?.id : ''}:${tokenEmployee}`}
                                uploadData={{
                                    resumeProcessId,
                                    retryProcessing,
                                    files: files.map(f => f.file),
                                    fileTypes: files.map(f => f.fileType || 'room_photo'),
                                    comment: isAdditional ? comment.trim() : 'Дополнение обмера',
                                    additionalCycleId: isAdditional ? cycle.id : undefined,
                                    eventId: selectedEvent.id,
                                    dealId: selectedEvent.bitrix_deal?.id,
                                    token: tokenEmployee,
                                }}
                                onDone={handleDone}
                                onBack={result => {
                                    setResumeProcessId(result && Object.prototype.hasOwnProperty.call(result, 'process_id') ? result.process_id : resumeProcessId);
                                    setRetryProcessing(false); setPhase('idle');
                                    setProcessCheckKey(value => value + 1);
                                }}
                                onBusyChange={setUploadInFlight}
                            />
                        ) : (
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                                <CircularProgress size="md" />
                                <Typography level="body-sm" sx={{ mt: 1.5 }}>Загружаем данные события...</Typography>
                            </Box>
                        )
                    )}
                </Sheet>
            )}
        </Box>
    );
}
